import { defineStore } from 'pinia'
import { useAppStore } from './app'

export const useDexStore = defineStore('dex', {
  state: () => ({
    // Pares de trading disponíveis
    tradingPairs: [],
    selectedPair: null,
    
    // Livro de ordens (orderbook)
    orderBook: {
      asks: [], // ordens de venda
      bids: []  // ordens de compra
    },
    
    // Histórico de trades
    recentTrades: [],
    
    // Gráfico de preços
    priceHistory: {
      timeframe: '1h', // 1m, 5m, 15m, 1h, 4h, 1d, 1w
      candles: [] // dados OHLCV (Open, High, Low, Close, Volume)
    },
    
    // Carteira do usuário
    wallet: {
      connected: false,
      address: null,
      balances: {},
      openOrders: []
    },
    
    // Estado de carregamento
    isLoadingPairs: false,
    isLoadingOrderBook: false,
    isLoadingTrades: false,
    isLoadingPriceHistory: false,
    isLoadingWallet: false,
    
    // Estado de operações
    isSubmittingOrder: false,
    isCancellingOrder: false
  }),
  
  getters: {
    // Getters para pares de trading
    availablePairs: (state) => {
      return state.tradingPairs.map(pair => ({
        id: pair.id,
        name: `${pair.baseToken.symbol}/${pair.quoteToken.symbol}`,
        baseSymbol: pair.baseToken.symbol,
        quoteSymbol: pair.quoteToken.symbol
      }))
    },
    
    currentPairName: (state) => {
      if (!state.selectedPair) return '-'
      return `${state.selectedPair.baseToken.symbol}/${state.selectedPair.quoteToken.symbol}`
    },
    
    // Getters para o livro de ordens
    topBid: (state) => {
      if (state.orderBook.bids.length === 0) return null
      return state.orderBook.bids[0]
    },
    
    topAsk: (state) => {
      if (state.orderBook.asks.length === 0) return null
      return state.orderBook.asks[0]
    },
    
    currentSpread: (state) => {
      const topAsk = state.orderBook.asks[0]
      const topBid = state.orderBook.bids[0]
      
      if (!topAsk || !topBid) return null
      
      const spread = topAsk.price - topBid.price
      const spreadPercentage = (spread / topBid.price) * 100
      
      return {
        value: spread,
        percentage: spreadPercentage
      }
    },
    
    orderBookDepth: (state) => {
      const bidVolume = state.orderBook.bids.reduce((sum, order) => sum + order.amount, 0)
      const askVolume = state.orderBook.asks.reduce((sum, order) => sum + order.amount, 0)
      
      return {
        bids: bidVolume,
        asks: askVolume,
        total: bidVolume + askVolume
      }
    },
    
    // Getters para a carteira
    isWalletConnected: (state) => state.wallet.connected,
    
    userBalances: (state) => {
      return state.wallet.balances
    },
    
    hasOpenOrders: (state) => state.wallet.openOrders.length > 0,
    
    // Getters para o gráfico de preços
    currentPrice: (state) => {
      if (state.priceHistory.candles.length === 0) return null
      return state.priceHistory.candles[state.priceHistory.candles.length - 1].close
    },
    
    priceChangePercentage: (state) => {
      if (state.priceHistory.candles.length < 2) return null
      
      const lastPrice = state.priceHistory.candles[state.priceHistory.candles.length - 1].close
      const firstPrice = state.priceHistory.candles[0].open
      
      const change = ((lastPrice - firstPrice) / firstPrice) * 100
      return parseFloat(change.toFixed(2))
    }
  },
  
  actions: {
    // Ações para carregar pares de trading
    async fetchTradingPairs() {
      const appStore = useAppStore()
      this.isLoadingPairs = true
      appStore.setLoading(true)
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.dex.obterPares()
        
        let pairs = data
        if (data && data.pairs) pairs = data.pairs
        if (!Array.isArray(pairs)) pairs = []
        
        this.tradingPairs = pairs
        
        if (!this.selectedPair && pairs.length > 0) {
          this.selectedPair = pairs[0]
        }
      } catch (error) {
        console.debug('[DEX] Trading pairs load failed:', error.message)
        this.tradingPairs = []
      } finally {
        this.isLoadingPairs = false
        appStore.setLoading(false)
      }
    },
    
    selectTradingPair(pairId) {
      const pair = this.tradingPairs.find(p => p.id === pairId)
      if (pair) {
        this.selectedPair = pair
        
        // Recarrega dados do par selecionado
        this.fetchOrderBook()
        this.fetchRecentTrades()
        this.fetchPriceHistory()
      }
    },
    
    // Ações para carregar o livro de ordens
    async fetchOrderBook() {
      if (!this.selectedPair) return
      
      const appStore = useAppStore()
      this.isLoadingOrderBook = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.dex.obterLivroDeOrdens(this.selectedPair.id)
        
        this.orderBook = {
          asks: data?.asks || [],
          bids: data?.bids || []
        }
      } catch (error) {
        console.debug('[DEX] Orderbook load failed:', error.message)
        this.orderBook = { asks: [], bids: [] }
      } finally {
        this.isLoadingOrderBook = false
      }
    },
    
    // Ações para carregar histórico de negociações recentes
    async fetchRecentTrades() {
      if (!this.selectedPair) return
      
      this.isLoadingTrades = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.dex.obterNegociacoesRecentes(this.selectedPair.id, 20)
        this.recentTrades = Array.isArray(data) ? data : (data?.trades || [])
      } catch (error) {
        console.debug('[DEX] Recent trades load failed:', error.message)
        this.recentTrades = []
      } finally {
        this.isLoadingTrades = false
      }
    },
    
    // Ações para carregar histórico de preços (candles)
    async fetchPriceHistory(timeframe = null) {
      if (!this.selectedPair) return
      
      if (timeframe) {
        this.priceHistory.timeframe = timeframe
      }
      
      this.isLoadingPriceHistory = true
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const data = await ExplorerAPI.dex.obterHistoricoPrecos(this.selectedPair.id, this.priceHistory.timeframe)
        this.priceHistory.candles = Array.isArray(data) ? data : (data?.candles || [])
      } catch (error) {
        console.debug('[DEX] Price history load failed:', error.message)
        this.priceHistory.candles = []
      } finally {
        this.isLoadingPriceHistory = false
      }
    },
    
    // Ações para conectar carteira
    async connectWallet() {
      const appStore = useAppStore()
      
      // Simulação de conexão com carteira
      this.isLoadingWallet = true
      
      setTimeout(() => {
        try {
          // Em um ambiente real, aqui seria a conexão com MetaMask, WalletConnect, etc.
          const address = 'Q' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
          
          this.wallet.connected = true
          this.wallet.address = address
          this.wallet.balances = {
            [window.__QUARNX_COIN__ || 'tKinX']: 1250.5,
            'QUSD': 5280.75,
            'ETH': 2.45,
            'BNB': 8.32
          }
          
          this.isLoadingWallet = false
          
          appStore.addNotification({
            type: 'success',
            message: 'Carteira conectada com sucesso',
            timeout: 3000
          })
          
          // Carregar ordens abertas da carteira
          this.fetchOpenOrders()
          
          return { success: true, address }
        } catch (e) {
          this.isLoadingWallet = false
          
          appStore.addNotification({
            type: 'error',
            message: `Erro ao conectar carteira: ${e.message}`,
            timeout: 5000
          })
          
          return { success: false, error: e.message }
        }
      }, 1000)
    },
    
    // Método para definir informações da carteira
    setWalletInfo(walletInfo) {
      // Atualiza as informações da carteira com os dados fornecidos
      if (walletInfo.address) {
        this.wallet.address = walletInfo.address
      }
      
      if (walletInfo.connected !== undefined) {
        this.wallet.connected = walletInfo.connected
      }
      
      if (walletInfo.balance !== undefined) {
        this.wallet.balances = {
          [window.__QUARNX_COIN__ || 'tKinX']: walletInfo.balance,
          'QUSD': 5280.75,
          'ETH': 2.45,
          'BNB': 8.32
        }
      }
      
      const appStore = useAppStore()
      appStore.addNotification({
        type: 'success',
        message: 'Carteira conectada com sucesso',
        timeout: 3000
      })
      
      this.isLoadingWallet = false
    },
    
    disconnectWallet() {
      this.wallet = {
        connected: false,
        address: null,
        balances: {},
        openOrders: []
      }
      
      const appStore = useAppStore()
      appStore.addNotification({
        type: 'info',
        message: 'Carteira desconectada',
        timeout: 3000
      })
    },
    
    // Ações para enviar ordens
    async submitOrder(orderData) {
      if (!this.wallet.connected || !this.selectedPair) {
        const appStore = useAppStore()
        appStore.addNotification({
          type: 'warning',
          message: 'Conecte sua carteira para negociar',
          timeout: 5000
        })
        return false
      }
      
      const appStore = useAppStore()
      this.isSubmittingOrder = true
      appStore.setLoading(true)
      
      try {
        const { ExplorerAPI } = await import('@/services/api')
        const result = await ExplorerAPI.dex.enviarOrdem({
          pair: this.selectedPair.id,
          side: orderData.side,
          type: orderData.type,
          price: orderData.price,
          amount: orderData.amount,
        })
        
        // Adiciona ordem à lista de ordens abertas
        if (result) {
          this.wallet.openOrders.unshift({
            id: result.id || 'order-' + Date.now(),
            pair: this.selectedPair.id,
            side: orderData.side,
            type: orderData.type,
            price: orderData.price,
            amount: orderData.amount,
            filled: 0,
            status: 'open',
            createdAt: new Date()
          })
        }
        
        // Atualiza o livro de ordens
        this.fetchOrderBook()
        
        appStore.addNotification({
          type: 'success',
          message: `Ordem de ${orderData.side === 'buy' ? 'compra' : 'venda'} enviada com sucesso`,
          timeout: 5000
        })
        
        return true
        
      } catch (error) {
        console.error('Erro ao enviar ordem:', error)
        appStore.addNotification({
          type: 'error',
          message: 'Falha ao enviar ordem',
          timeout: 5000
        })
        return false
      } finally {
        this.isSubmittingOrder = false
        appStore.setLoading(false)
      }
    },
    
    // Cancelar ordem
    async cancelOrder(orderId) {
      if (!this.wallet.connected) return false
      
      const appStore = useAppStore()
      this.isCancellingOrder = true
      
      try {
        // Simulando cancelamento de ordem
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Remove a ordem da lista de ordens abertas
        const orderIndex = this.wallet.openOrders.findIndex(order => order.id === orderId)
        if (orderIndex !== -1) {
          this.wallet.openOrders.splice(orderIndex, 1)
        }
        
        // Atualiza o livro de ordens
        this.fetchOrderBook()
        
        appStore.addNotification({
          type: 'success',
          message: 'Ordem cancelada com sucesso',
          timeout: 3000
        })
        
        return true
        
      } catch (error) {
        console.error('Erro ao cancelar ordem:', error)
        appStore.addNotification({
          type: 'error',
          message: 'Falha ao cancelar ordem',
          timeout: 5000
        })
        return false
      } finally {
        this.isCancellingOrder = false
      }
    },
    
    // Inicialização - carrega dados iniciais
    async initializeData() {
      await this.fetchTradingPairs()
      
      if (this.selectedPair) {
        await Promise.all([
          this.fetchOrderBook(),
          this.fetchRecentTrades(),
          this.fetchPriceHistory()
        ])
      }
    }
  }
})
